import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetAllSitesQuery } from '@/hooks/queries/use-get-all-sites';
import AddressForm from '../addess/address-form';
import { AddOfficeUseForm } from './use-add-office-form';

type Props = {
  form: AddOfficeUseForm;
  showSiteSelect?: boolean;
  /**
   * Make sensitive financial fields (retrocession rates, estimated CA per slot,
   * projected honorary) read-only. Used when a user without
   * can_edit_office_financials (e.g. csm) edits an existing office.
   */
  disableFinancialFields?: boolean;
};

const AddOfficeForm = ({
  form,
  showSiteSelect = true,
  disableFinancialFields = false,
}: Props) => {
  const { t } = useTranslation();
  const { data: sites = [], isLoading: sitesLoading } = useGetAllSitesQuery({
    enabled: showSiteSelect,
  });

  return (
    <Form {...form}>
      <form className="grid gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>{t('add-office-form.name.label')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        ></FormField>

        {showSiteSelect && (
          <FormField
            control={form.control}
            name="siteId"
            render={({ field }) => (
              <FormItem className="grid gap-2">
                <FormLabel>{t('add-office-form.site.label')}</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value}
                  disabled={sitesLoading}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          sitesLoading
                            ? t('add-office-form.site.loading')
                            : t('add-office-form.site.placeholder')
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem
                        key={site.id}
                        value={site.id}
                      >
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="nationalMedicalOrderMail"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.national-medical-order-mail.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t(
                    'add-office-form.national-medical-order-mail.placeholder',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        ></FormField>

        <AddressForm form={form as any} />

        {/* Estimated CA per slot */}
        <FormField
          control={form.control}
          name="estimatedCaPerSlot"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.estimated-ca-per-slot.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder={t(
                    'add-office-form.estimated-ca-per-slot.placeholder',
                  )}
                  {...field}
                  disabled={disableFinancialFields}
                />
              </FormControl>
              <FormDescription>
                {t('add-office-form.estimated-ca-per-slot.description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="averageSlotHonorary"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.average-slot-honorary.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder={t(
                    'add-office-form.average-slot-honorary.placeholder',
                  )}
                  {...field}
                  disabled={disableFinancialFields}
                />
              </FormControl>
              <FormDescription>
                {t('add-office-form.average-slot-honorary.description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="withAssistantRetrocessionRate"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.with-assistant-retrocession-rate.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t(
                    'add-office-form.with-assistant-retrocession-rate.placeholder',
                  )}
                  {...field}
                  disabled={disableFinancialFields}
                />
              </FormControl>
              <FormDescription>
                {t(
                  'add-office-form.with-assistant-retrocession-rate.description',
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="withoutAssistantRetrocessionRate"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.without-assistant-retrocession-rate.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t(
                    'add-office-form.without-assistant-retrocession-rate.placeholder',
                  )}
                  {...field}
                  disabled={disableFinancialFields}
                />
              </FormControl>
              <FormDescription>
                {t(
                  'add-office-form.without-assistant-retrocession-rate.description',
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="weekendRetrocessionRate"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('add-office-form.weekend-retrocession-rate.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t(
                    'add-office-form.weekend-retrocession-rate.placeholder',
                  )}
                  {...field}
                  disabled={disableFinancialFields}
                />
              </FormControl>
              <FormDescription>
                {t('add-office-form.weekend-retrocession-rate.description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default AddOfficeForm;
